import { Button } from 'cpool-components/input/Button';
import { CHILD_BENEFIT_TYPES } from 'cpool-constants';
import type { ApplicantCreation } from 'cpool-schemas';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus, LuTrash2 } from 'react-icons/lu';
import { v4 as uuidv4 } from 'uuid';

import { FormCurrencyInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormCurrencyInput';
import { FormNumberInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormNumberInput';
import { FormCard } from '~/features/leadDetails/components/Overview/CustomerData/shared/FormCard';
import { useApplicantAttribution } from '../../util/hooks/useApplicantAttribution';
import { KINDERGELD_HOEHE } from '~/constants';

export const Children: React.FC = () => {
    const { t } = useTranslation();
    const { control } = useFormContext<ApplicantCreation>();
    const attribution = useApplicantAttribution();

    const { append, fields, remove } = useFieldArray<ApplicantCreation, 'kinder', 'childId'>({
        control,
        keyName: 'childId',
        name: 'kinder',
    });

    const addChild = () => {
        append({
            alter: null,
            gehoertZuAntragsteller: attribution,
            id: uuidv4(),
            kindergeldFuer: CHILD_BENEFIT_TYPES.ERSTES_ODER_ZWEITES_KIND,
            kindergeldHoehe: KINDERGELD_HOEHE,
            unterhaltseinnahmenMonatlich: null,
        });
    };

    return (
        <div className="flex flex-col gap-2">
            {fields.map((field, index) => (
                <FormCard key={field.childId} className="relative">
                    <div className="absolute right-1.5 top-1.5 flex">
                        <button
                            type="button"
                            onClick={() => remove(index)}
                            className="rounded p-1 text-alert-error hover:bg-alert-error/10"
                        >
                            <LuTrash2 className="h-4 w-4" />
                        </button>
                    </div>
                    <p className="text-center text-sm text-midnight">
                        {t('LIVING_SITUATION_DATA.CHILDREN.CHILDREN_TITLE', {
                            childNr: (index + 1).toString(),
                        })}
                    </p>

                    <FormNumberInput
                        id={`kinder.${index}.alter`}
                        label={t('LIVING_SITUATION_DATA.CHILDREN.CHILD_AGE')}
                        control={control}
                        allowDecimals={false}
                        min={0}
                    />

                    <FormCurrencyInput
                        id={`kinder.${index}.kindergeldHoehe`}
                        label={t('LIVING_SITUATION_DATA.CHILDREN.CHILD_SUPPORT')}
                        control={control}
                        min={0}
                    />

                    <FormCurrencyInput
                        id={`kinder.${index}.unterhaltseinnahmenMonatlich`}
                        label={t('LIVING_SITUATION_DATA.CHILDREN.MAINTENANCE_INCOME')}
                        control={control}
                        min={0}
                    />
                </FormCard>
            ))}

            <div className="flex justify-center py-2">
                <Button type="button" variant="secondary" onClick={addChild} iconLeft={<LuPlus className="h-4 w-4" />}>
                    {t('LIVING_SITUATION_DATA.CHILDREN.ADD_CHILD')}
                </Button>
            </div>
        </div>
    );
};
